import React, { FC, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Theme, Typography, useMediaQuery } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import Link from 'next/link';
import {
	FileVariantsEnum,
	getEventShortLocalInstance,
	getRecipientOfTicketEmail,
	UserDto,
	UserToMembershipDto,
} from '@sweatpals/shared';
import { observer } from 'mobx-react-lite';
import { EventBackground, HostBackground, Showable } from '@sweatpals/react-ui-kit';
import { authStore } from 'stores';
import { EventType } from '../../../services/types/event-types';
import { ENABLE_MEMBERSHIP_UI } from '../../../constants/features';
import RemoteImage from '../../images/Remote-image';
import { MembershipInfoTable } from '../membership-form-modal/membership-info-table.component';
import ExternalLink from '../../links/External-link';
import { LockIcon } from '../../icons';
import CommunityEvent from '../community-events/community-event.component';
import EventActionModal from '../../ui-v2/event-action-modal/event-action-modal.component';
import useLink from '../../../utils/hooks/useLink';
import { eventClassesService, eventsService, paymentService } from '../../../services';
import { queryKeys } from '../../../constants';
import { useStyles } from '../../../utils/hooks/useStyles';
import { orderStyles } from './order.styles';
import { getPublicFileUrl } from '../../../utils/file.utils';
import { FadeSpinner } from '../fade-spinner';
import UpgradeTicketFormModal from '../upgrade-ticket-form-modal/upgrade-ticket-form-modal.component';
import { EventTicketCard } from './event-ticket-card.component';
import OrderLoader from './order.loader';

export interface OrderProps {
	orderId?: string;
	host?: UserDto;
	coHost?: UserDto;
	hostEvents?: EventType[];
	coHostEvents?: EventType[];
	eventData?: EventType;
	userMembership?: UserToMembershipDto;
	Header?: FC;
}

// eslint-disable-next-line complexity
export const Order: FC<OrderProps> = observer(props => {
	const {
		host,
		coHost,
		hostEvents,
		coHostEvents,
		eventData,
		orderId,
		userMembership,
		Header,
	} = props;

	const router = useRouter();
	const { enqueueSnackbar } = useSnackbar();
	const user = authStore.user;

	const [showGroupChatModal, setShowGroupChatModal] = useState(false);
	const [fetchedInvitationCode, setFetchedInvitationCode] = useState(false);
	const [showContactHostModal, setShowContactHostModal] = useState(false);
	const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
	const { makeHostLink, appLinkByOs } = useLink();

	const { data: order, isLoading: orderIsLoading, refetch: refetchOrder } = useQuery(
		queryKeys.order(orderId),
		async () => (orderId ? paymentService.getOrderByIdOrPaymentIntentId(orderId) : null),
		{
			onError: (error: AxiosError<{ message: string }>) => {
				enqueueSnackbar(error.response?.data?.message || error.message, { variant: 'error' });
			},
			refetchInterval: (order) => { // retry until order has any tickets
				if(order?.tickets?.length) {
					return false;
				}

				return 5000;
			}
		}
	);

	const shortEventInstance = useMemo(() => order?.event && getEventShortLocalInstance(order.event, order.eventInstanceDate), [order]);
	const selectedTicket = useMemo(() => (order?.tickets ?? []).find(ticket => ticket.id === selectedTicketId), [order, selectedTicketId]);

	const onLinkClick = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();

		if (e.currentTarget.href) {
			router.push(e.currentTarget.href);
		}
	}, [router]);

	const { data: eventChatInviteCode = '', mutate } = useMutation(
		['eventChatInviteCode', eventData?.eventChatCid],
		async (eventId: string) => eventsService.getEventGroupChatInvitationCode(eventId)
	);
	const { data: eventClasses, isLoading: eventClassesIsLoading } = useQuery(
		queryKeys.eventClasses(order?.eventId, shortEventInstance),
		async () => (order?.eventId && shortEventInstance ? eventClassesService.getEventClasses(order.eventId, shortEventInstance) : []),
		{
			onError: (error: AxiosError<{ message: string }>) => {
				enqueueSnackbar(error.response?.data?.message || error.message, { variant: 'error' });
			},
		}
	);

	useEffect(() => {
		if (eventData?.eventChatCid && !fetchedInvitationCode) {
			mutate(eventData?.id);
			setFetchedInvitationCode(true);
		}
	}, [eventData?.eventChatCid, eventData?.id, fetchedInvitationCode, mutate]);

	const styles = useStyles(orderStyles);

	const hostPageUrl = useMemo(() => makeHostLink({ userName: host?.userName }), [host?.userName, makeHostLink]);
	const coHostPageUrl = useMemo(() => makeHostLink({ userName: coHost?.userName }), [coHost?.userName, makeHostLink]);
	const eventBackgroundUrl = useMemo(
		() =>
			eventData?.background
			&& getPublicFileUrl(eventData.background, {
				variant: FileVariantsEnum.S,
			}),
		[eventData?.background]
	);

	const hostBackgroundUrl = useMemo(
		() =>
			(host?.backgroundId
				? getPublicFileUrl(host?.backgroundId, {
					variant: FileVariantsEnum.S,
				}) : undefined),
		[host?.backgroundId]
	);

	const receiptsEmail = useMemo(() => getRecipientOfTicketEmail(order, user), [order, user]);

	const mdMedia = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

	const openGroupChatModal = useCallback(() => {
		setShowGroupChatModal(true);
	}, []);

	const closeGroupChatModal = useCallback(() => {
		setShowGroupChatModal(false);
	}, []);

	const openContactHostModal = useCallback(() => {
		setShowContactHostModal(true);
	}, []);

	const closeContactHostModal = useCallback(() => {
		setShowContactHostModal(false);
	}, []);

	return (
		<Box sx={styles.root}>
			{eventData ? (
				<EventBackground background={eventBackgroundUrl} />
			) : (
				<HostBackground background={hostBackgroundUrl} primaryColor={host?.primaryColor} />
			)}

			<Showable show={orderIsLoading || eventClassesIsLoading}>
				<Box sx={styles.content}>
					{Header && <Header />}
					<OrderLoader />
				</Box>
			</Showable>

			<Showable hide={orderIsLoading || eventClassesIsLoading}>
				<Box sx={styles.content}>
					{Header && <Header />}
					<Box display="flex" justifyContent="center" marginTop="50px">
						<Box sx={{ width: '945px', display: 'flex', flexDirection: 'column', gap: '42px' }}>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '17px' }} id="order-success-info">
								{userMembership && ENABLE_MEMBERSHIP_UI ? (
									<>
										<Box sx={styles.membershipHeaderBox}>
											<Box sx={styles.membershipHostImageBox}>
												<RemoteImage
													src={host?.avatarId}
													alt="host avatar"
													width={90}
													height={90}
													sx={{
														borderRadius: '50%',
													}}
												/>
											</Box>
											<Box sx={styles.membershipTitleBox}>
												<Typography sx={styles.membershipTitle}>
													It’s offical! Welcome to {host?.fullName}!
												</Typography>
												<Typography sx={styles.membershipDescription}>
													We have confirmed your membership.
												</Typography>
											</Box>
										</Box>

										<Box sx={styles.membershipBodyBox}>
											<Box sx={styles.membershipBodyHeader}>
												<Typography sx={styles.membershipHeaderTitle}>{userMembership.membership.name}</Typography>

												<Typography sx={styles.membershipHeaderDescription}>
													After logging in, you&apos;ll have the option to RSVP for events hosted by this organizer for
													free using your membership.
												</Typography>
											</Box>

											<MembershipInfoTable userMembership={userMembership} />
										</Box>

										<Showable show={order?.tickets?.length && eventData}>
											<Typography sx={styles.title}>Thanks for your order!</Typography>
										</Showable>
									</>
								) : (
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
										<Typography sx={styles.title}>Thanks for your order!</Typography>
										<Showable show={order?.user?.receiptsEmail}>
											<Typography sx={styles.description}>
												You will receive an email with your ticket and confirmation on {receiptsEmail}
											</Typography>
										</Showable>
									</Box>
								)}

								<Showable show={order?.tickets?.length && eventData}>
									<Box sx={styles.ticketsBox}>
										<Showable show={mdMedia}>
											<Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
												<Typography sx={styles.appLink}>
													<ExternalLink href={appLinkByOs}>Download Sweatpals app</ExternalLink> to get the latest event
													announcements, meet other attendees and more.
												</Typography>
												<Button sx={styles.hostButton} onClick={openContactHostModal}>
													Contact Host
												</Button>
											</Box>
										</Showable>
										<Box sx={styles.tickets}>
											{order?.tickets?.map(ticket => (
												<EventTicketCard
													key={ticket.id}
													ticket={ticket}
													order={order}
													eventData={eventData}
													eventClasses={eventClasses}
													host={host}
													coHost={coHost}
													isNewTicket
													onUpgradeTicketButtonClick={setSelectedTicketId}
													onTicketTransferButtonClick={(ticketId) => {
														window.open(`/order/${order.id}/transfer-ticket?ticketId=${ticketId}`, '_blank');
													}}
												/>
											))}
										</Box>
									</Box>
								</Showable>
								<Showable hide={mdMedia}>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: '16px',
											backgroundColor: '#13131380',
											p: '16px',
											border: '1px solid #00000080',
											borderRadius: '8px',
										}}
									>
										<Typography sx={styles.appLink}>
											<ExternalLink href={appLinkByOs}>Download Sweatpals app</ExternalLink> to manage your tickets more
											easily
										</Typography>
										<Box
											sx={{
												display: 'flex',
												justifyContent: 'space-between',
												flexDirection: { xs: 'column', sm: 'row' },
												alignItems: 'center',
												gap: 1
											}}
										>
											<Button sx={{ ...styles.button, ...styles.secondaryButton }} onClick={openGroupChatModal}>
												<LockIcon />
												group chat
											</Button>
											<Button sx={styles.button} onClick={openContactHostModal}>
												Contact Host
											</Button>
										</Box>
									</Box>
									<Showable show={hostEvents?.length || coHostEvents?.length}>
										<Box sx={{ width: '100%', borderBottom: '1px solid #333333' }} />
									</Showable>
								</Showable>
							</Box>
							<Showable show={hostEvents?.length}>
								<Box display="flex" flexDirection="column" gap="26px">
									<Box
										sx={{
											gap: '16px',
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: { xs: 'flex-start', sm: 'center' },
											flexDirection: { xs: 'column', sm: 'row' },
											px: { xs: '16px', sm: '0' }
										}}
									>
										<Typography sx={styles.communityEventsTitle}>
											More events from <Link href={hostPageUrl} onClick={onLinkClick} style={{ color: 'white' }} >{host?.fullName}</Link>
										</Typography>
										<Link href={hostPageUrl} passHref legacyBehavior onClick={onLinkClick} >
											<Box sx={styles.link}>See All</Box>
										</Link>
									</Box>
									{hostEvents?.map(event => (
										<CommunityEvent key={event.id} event={event} />
									))}
								</Box>
							</Showable>
							<Showable show={coHostEvents?.length}>
								<Box display="flex" flexDirection="column" gap="26px">
									<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: '16px', px: { xs: '16px', sm: '0' } }}>
										<Typography sx={styles.communityEventsTitle}>
											More events from <span>{coHost?.fullName}</span>
										</Typography>
										<Link href={coHostPageUrl} passHref legacyBehavior onClick={onLinkClick} >
											<Box sx={styles.link}>See All</Box>
										</Link>
									</Box>
									{coHostEvents?.map(event => (
										<CommunityEvent key={event.id} event={event} />
									))}
								</Box>
							</Showable>
						</Box>
					</Box>
				</Box>
				<Showable show={selectedTicket}>
					<UpgradeTicketFormModal
						open={true}
						ticket={selectedTicket}
						eventData={eventData}
						refetchOrder={refetchOrder}
						onClose={() => {
							setSelectedTicketId(null);
						}}
					/>
				</Showable>
				<EventActionModal
					open={showContactHostModal}
					onClose={closeContactHostModal}
					hostId={eventData?.authorId}
					type="host"
					text={
						<>
							Message the host on <br /> the SweatPals app
						</>
					}
				/>
				<EventActionModal
					open={showGroupChatModal}
					onClose={closeGroupChatModal}
					inviteCode={eventChatInviteCode}
					type="chat"
					text={
						<>
							Join hundreds of <br /> communities on the <br /> SweatPals app
						</>
					}
				/>
			</Showable>
		</Box>
	);
});
